import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { HOME } from '@/data/home';
import { GALLERY as GALLERY_POOL } from '@/data/resources';
import { SITE_STATE } from '@/lib/siteContent';
import { Reveal } from '@components/motion';
import { Lightbox, type LightboxContent } from '@components/pages/ResourceActions';
import type { GalleryItem } from '@components/pages/ResourceSections';
import { onImageError } from '@/lib/imageFallback';
import FigmaFrame from './FigmaFrame';
import { Img } from '@/components/Img';

const VIDEO_HERO = '/landing/video/video-hero.webp';
const GALLERY_LEFT = '/landing/video/gallery-left.webp';
const GALLERY_MID_TOP = '/landing/video/gallery-mid-top.webp';
const GALLERY_MID_BOTTOM = '/landing/video/gallery-mid-bottom.webp';
const GALLERY_RIGHT = '/landing/video/gallery-right.webp';

/** What the band shows if the CMS has featured nothing yet. */
const FALLBACK_FILMS: GalleryItem[] = [
    { slug: 'film', kind: 'video', image: VIDEO_HERO, title: 'A Day With The Duty Solicitors' },
    { slug: 'film-2', kind: 'video', image: VIDEO_HERO, title: 'Justice In Motion' },
    { slug: 'film-3', kind: 'video', image: VIDEO_HERO, title: 'Voices From The Field' },
];
const FALLBACK_FRAMES: GalleryItem[] = [GALLERY_LEFT, GALLERY_MID_TOP, GALLERY_MID_BOTTOM, GALLERY_RIGHT].map(
    (image, i) => ({ slug: `frame-${i + 1}`, kind: 'image', image, title: '' }),
);

/**
 * The film band takes the featured videos and the four frames the featured
 * images, in the order the CMS lists them. Read at render, not at import: the
 * pool is filled in by the CMS after this module loads.
 */
const featuredFilms = (): GalleryItem[] => {
    const films = GALLERY_POOL.filter((g) => g.kind === 'video' && g.featured);
    return films.length ? films.slice(0, 3) : FALLBACK_FILMS;
};

/**
 * Four frame slots. Once the CMS has answered they hold only what an editor
 * featured — a slot with nothing in it stays dark rather than showing a stock
 * photograph as if it were the organisation's own. Offline, the bundled art
 * stands as the page shipped.
 */
const featuredFrames = (): (GalleryItem | null)[] => {
    const frames = GALLERY_POOL.filter((g) => g.kind === 'image' && g.featured).slice(0, 4);
    if (!SITE_STATE.cmsAnswered) return FALLBACK_FRAMES;
    return [...frames, ...Array<null>(Math.max(0, 4 - frames.length)).fill(null)];
};

/** A frame, or a quiet dark panel when the slot is empty. */
function Frame({ item, className }: { item: GalleryItem | null; className?: string }) {
    if (!item) return <div className={`absolute inset-0 bg-[#141414] ${className ?? ''}`} aria-hidden />;
    return (
        <Img
            src={item.image}
            alt={item.caption ?? ''}
            className={`absolute inset-0 size-full max-w-none object-cover ${className ?? ''}`}
        />
    );
}

/** The band's two-tone title, written on the Home page in the CMS. */
function BandTitle({ desktop }: { desktop: boolean }) {
    return desktop ? (
        <div className="flex flex-col justify-center text-[56px] text-[#1b1b1b]">
            <p className="mb-0 font-light leading-[72px]">{HOME.video.heading}</p>
            <p className="leading-[72px]">
                <span className="text-[#1b1b1b]">{HOME.video.headingBold}</span>
                <span className="text-[#f60]">.</span>
            </p>
        </div>
    ) : (
        <div className="px-5 pt-10 text-[34px] text-[#1b1b1b]">
            <span className="block font-light leading-[46px]">{HOME.video.heading}</span>
            <span className="block font-extrabold leading-[46px]">
                {HOME.video.headingBold}
                <span className="text-[#f60]">.</span>
            </span>
        </div>
    );
}

/** A film with a link plays in the lightbox; without one there is nothing to open. */
const toPreview = (film: GalleryItem): LightboxContent | null =>
    film.videoUrl ? { title: film.title, image: film.image, videoUrl: film.videoUrl, caption: film.caption } : null;

/** Orange play button (circle + triangle) recreated from Figma vectors. */
function PlayButton({ className, onClick, label }: { className?: string; onClick?: () => void; label: string }) {
    return (
        <button
            type="button"
            aria-label={`Play ${label}`}
            onClick={onClick}
            disabled={!onClick}
            className={`${className ?? ''} transition-transform duration-300 hover:scale-105 disabled:cursor-default disabled:hover:scale-100`}
        >
            <svg viewBox="0 0 69 69" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-full">
                <circle cx="34.5" cy="34.5" r="34" fill="#FF6600" fillOpacity="0.09" stroke="#FF6600" />
                <path d="M47 35L29 45.3923V24.6077L47 35Z" fill="#FF6600" />
            </svg>
        </button>
    );
}

/** Chevron used by the carousel arrows. `direction` rotates the upward chevron. */
function Chevron({ direction, color }: { direction: 'left' | 'right'; color: string }) {
    return (
        <span
            className={`inline-flex size-[41px] items-center justify-center ${direction === 'left' ? '-rotate-90' : 'rotate-90'}`}
        >
            <svg
                viewBox="0 0 19.6716 11.8132"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="block w-[19.67px]"
            >
                <path
                    d="M10.5429 5.07623C10.1524 4.6857 9.5192 4.6857 9.12868 5.07623L2.68456 11.5203C2.29403 11.9109 1.66087 11.9109 1.27035 11.5203L0.292894 10.5429C-0.0976308 10.1524 -0.0976314 9.5192 0.292893 9.12868L9.12868 0.292893C9.5192 -0.0976311 10.1524 -0.0976311 10.5429 0.292893L19.3787 9.12868C19.7692 9.5192 19.7692 10.1524 19.3787 10.5429L18.4012 11.5203C18.0107 11.9109 17.3776 11.9109 16.987 11.5203L10.5429 5.07623Z"
                    fill={color}
                />
            </svg>
        </span>
    );
}

function VideoSectionDesktop({ onPlay }: { onPlay: (film: GalleryItem) => void }) {
    const films = featuredFilms();
    const [filmIndex, setFilmIndex] = useState(0);
    const film = films[filmIndex % films.length];
    const stepFilm = (dir: number) => setFilmIndex((i) => (i + dir + films.length) % films.length);

    // The frame arrows turn the four frames round, so every one gets the large slot in its turn.
    const frames = featuredFrames();
    const [turn, setTurn] = useState(0);
    const shown = frames.filter(Boolean).length;
    const rotated = shown > 1 ? [...frames.slice(turn % 4), ...frames.slice(0, turn % 4)] : frames;
    const [left, midTop, midBottom, right] = rotated;
    return (
        <FigmaFrame designWidth={1920} designHeight={1101} className="w-full">
            <div className="relative h-[1101px] w-[1920px] bg-[#f9f9f9]">
                {/* Video hero raster — dark cinematic B&W treatment */}
                <div className="absolute left-0 top-0 h-[532px] w-[1920px] overflow-hidden bg-[#0a0a0a]">
                    <Img
                        src={film.image}
                        alt=""
                        onError={onImageError(VIDEO_HERO)}
                        className="pointer-events-none absolute inset-0 size-full max-w-none object-cover grayscale"
                        style={{ objectPosition: '50% 15%' }}
                    />
                    {/* Flat dark wash + bottom-heavy gradient */}
                    <div className="absolute inset-0 bg-black/45" />
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage:
                                'linear-gradient(2.142deg, rgba(6,6,6,0.55) 20%, rgba(4,4,4,0.45) 60%, rgba(0,0,0,0.25) 100%)',
                        }}
                    />
                </div>

                {/* Video title — lower-left of the banner */}
                <Reveal
                    as="div"
                    once
                    variant="fade-right"
                    className="absolute left-[146px] top-[266px] flex w-[371px] -translate-y-1/2 flex-col justify-center"
                >
                    <p className="line-clamp-3 text-[56px] font-extrabold leading-[72px] text-white">{film.title}</p>
                </Reveal>

                {/* Video paragraph — bottom-aligned with the title */}
                <div className="absolute left-[574px] top-[266px] flex h-[72px] w-[402px] -translate-y-1/2 flex-col justify-center">
                    <p className="text-[16px] font-normal leading-[24px] text-white">{HOME.video.paragraph}</p>
                </div>

                {/* Play button */}
                <PlayButton
                    className="absolute left-[1035px] top-[266px] size-[69px] -translate-y-1/2"
                    label={film.title}
                    onClick={film.videoUrl ? () => onPlay(film) : undefined}
                />

                {/* Hero carousel arrows — step through the featured films */}
                {films.length > 1 && (
                    <>
                        <button
                            type="button"
                            aria-label="Previous film"
                            onClick={() => stepFilm(-1)}
                            className="absolute left-[1703px] top-[266px] size-[41px] -translate-y-1/2"
                        >
                            <Chevron direction="left" color="#E0E0E0" />
                        </button>
                        <button
                            type="button"
                            aria-label="Next film"
                            onClick={() => stepFilm(1)}
                            className="absolute left-[1766px] top-[266px] size-[41px] -translate-y-1/2"
                        >
                            <Chevron direction="right" color="#FF6600" />
                        </button>
                    </>
                )}

                {/* Gallery: left (people, orange tint) */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    className="absolute left-[488px] top-[532px] h-[569px] w-[477px]"
                >
                    <Frame item={left} className="pointer-events-none" />
                    {left && <div className="absolute inset-0 bg-[#f60] mix-blend-multiply" />}
                </Reveal>
                {/* Caption overlay on left gallery image */}
                <div className="absolute left-[488px] top-[962px] h-[139px] w-[477px]">
                    <div className="absolute inset-0 bg-[rgba(27,27,27,0.5)] backdrop-blur-[11.65px]" />
                    <div className="absolute left-[29px] top-[70px] flex w-[420px] -translate-y-1/2 flex-col justify-center">
                        <p className="text-[16px] font-normal leading-[24px] text-white">{HOME.video.galleryCaption}</p>
                    </div>
                </div>

                {/* Gallery: middle top */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={120}
                    className="absolute left-[965px] top-[532px] h-[285px] w-[478px] bg-[#d9d9d9]"
                >
                    <Frame item={midTop} />
                </Reveal>
                {/* Gallery: middle bottom */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={200}
                    className="absolute left-[965px] top-[817px] h-[284px] w-[478px] bg-[#d9d9d9] mix-blend-luminosity"
                >
                    <Frame item={midBottom} />
                </Reveal>
                {/* Gallery: right */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={280}
                    className="absolute left-[1443px] top-[532px] h-[569px] w-[477px] bg-[#d9d9d9]"
                >
                    <Frame item={right} />
                </Reveal>

                {/* Left column copy */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    className="absolute left-[59px] top-[616px] flex w-[371px] flex-col items-start gap-[35px]"
                >
                    <BandTitle desktop />
                    <Link
                        to="/resources/gallery"
                        className="flex w-[216px] items-center justify-center rounded-[10px] bg-[#1b1b1b] px-[20px] py-[10px]"
                    >
                        <span className="whitespace-nowrap text-[16px] font-bold leading-[24px] text-white">
                            View All
                        </span>
                    </Link>
                    {shown > 1 && (
                        <div className="flex items-center gap-[22px]">
                            <button type="button" aria-label="Previous frames" onClick={() => setTurn((t) => t + 3)}>
                                <Chevron direction="left" color="#E0E0E0" />
                            </button>
                            <button type="button" aria-label="Next frames" onClick={() => setTurn((t) => t + 1)}>
                                <Chevron direction="right" color="#1B1B1B" />
                            </button>
                        </div>
                    )}
                </Reveal>
            </div>
        </FigmaFrame>
    );
}

function VideoSectionMobile({ onPlay }: { onPlay: (film: GalleryItem) => void }) {
    const films = featuredFilms();
    // On the phone the frames are a swipeable rail, so empty slots are simply not there.
    const frames = featuredFrames().filter((g): g is GalleryItem => g !== null);
    const videoRef = useRef<HTMLDivElement>(null);
    const galleryRef = useRef<HTMLDivElement>(null);
    const scrollBy = (ref: React.RefObject<HTMLDivElement | null>, dir: number) => {
        const el = ref.current;
        if (!el) return;
        el.scrollBy({ left: dir * el.clientWidth * 0.86, behavior: 'smooth' });
    };

    return (
        <section className="bg-white">
            {/* Video banner — swipeable strip of videos */}
            <div className="relative overflow-hidden bg-[#0a0a0a]">
                <Img
                    src={films[0].image}
                    alt=""
                    onError={onImageError(VIDEO_HERO)}
                    className="absolute inset-0 size-full object-cover grayscale"
                    style={{ objectPosition: '50% 15%' }}
                />
                <div className="absolute inset-0 bg-black/45" />
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage:
                            'linear-gradient(2.142deg, rgba(6,6,6,0.7) 33.814%, rgba(4,4,4,0.4) 56.618%, rgba(0,0,0,0) 93.022%)',
                    }}
                />

                <div
                    ref={videoRef}
                    className="scrollbar-hide relative flex snap-x snap-mandatory overflow-x-auto scroll-smooth"
                >
                    {films.map((film) => (
                        <div key={film.slug} className="flex w-full shrink-0 snap-start gap-4 px-5 pb-4 pt-8">
                            <PlayButton
                                className="mt-9 size-[46px] shrink-0"
                                label={film.title}
                                onClick={film.videoUrl ? () => onPlay(film) : undefined}
                            />
                            <div className="flex-1">
                                <p className="text-[20px] font-extrabold leading-[27px] text-white">{film.title}</p>
                                <p className="mt-3 text-[13px] font-normal leading-[20px] text-white/90">
                                    {HOME.video.paragraph}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Video controls */}
                <div className="relative flex items-center gap-[26px] px-5 pb-7 pl-[84px]">
                    <button type="button" aria-label="Previous video" onClick={() => scrollBy(videoRef, -1)}>
                        <Chevron direction="left" color="#E0E0E0" />
                    </button>
                    <button type="button" aria-label="Next video" onClick={() => scrollBy(videoRef, 1)}>
                        <Chevron direction="right" color="#FF6600" />
                    </button>
                </div>
            </div>

            {/* Heading */}
            <BandTitle desktop={false} />

            {/* Gallery — swipeable, next frame peeking */}
            <div
                ref={galleryRef}
                className="scrollbar-hide mt-7 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-5"
            >
                {frames.map((g, i) => (
                    <div
                        key={g.slug}
                        className="relative h-[190px] w-[86%] shrink-0 snap-start overflow-hidden bg-[#d9d9d9]"
                    >
                        <Img src={g.image} alt={g.caption ?? ''} className="absolute inset-0 size-full object-cover" />
                        {i === 0 && <div className="absolute inset-0 bg-[#f60] mix-blend-multiply" />}
                    </div>
                ))}
            </div>

            {/* View All + gallery controls */}
            <div className="flex flex-col items-center gap-7 px-5 pb-14 pt-8">
                <Link
                    to="/resources/gallery"
                    className="flex w-[170px] items-center justify-center rounded-[10px] bg-[#1b1b1b] px-5 py-3"
                >
                    <span className="text-[16px] font-bold leading-[24px] text-white">View All</span>
                </Link>
                <div className="flex items-center gap-[34px]">
                    <button type="button" aria-label="Previous images" onClick={() => scrollBy(galleryRef, -1)}>
                        <Chevron direction="left" color="#C4C4C4" />
                    </button>
                    <button type="button" aria-label="Next images" onClick={() => scrollBy(galleryRef, 1)}>
                        <Chevron direction="right" color="#1B1B1B" />
                    </button>
                </div>
            </div>
        </section>
    );
}

export default function VideoSection() {
    const [preview, setPreview] = useState<LightboxContent | null>(null);
    const play = (film: GalleryItem) => setPreview(toPreview(film));
    return (
        <>
            <div className="hidden lg:block">
                <VideoSectionDesktop onPlay={play} />
            </div>
            <div className="lg:hidden">
                <VideoSectionMobile onPlay={play} />
            </div>
            <Lightbox item={preview} onClose={() => setPreview(null)} />
        </>
    );
}
